import { differenceInDays, differenceInHours, differenceInMinutes } from "date-fns";
import {
  AlertCircle,
  ArrowRight,
  Bell,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  CheckCircle2,
  ClipboardList,
  Clock,
  Handshake,
  Link2,
  MessageCircle,
  PartyPopper,
  Send,
  Star,
  Trophy,
  UserCheck,
  UserPlus,
  UserX,
  Users,
} from "lucide-react";

import { Notification } from "@/services/api";
import { parseDateInput } from "@/utils/formatDisplayDate";

const MESSAGE_NOTIFICATION_TYPES = new Set(["new_direct_message", "unread_messages_digest"]);
const SESSION_PREP_NOTIFICATION_TYPES = new Set(["session_prep_ready", "prep_notes_submitted"]);

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
  /**
   * Overrides the session-prep call to action.
   *
   * Needed because the roles invert between the two program types: on a PAB the
   * achiever prepares the briefing and the advisors read it, whereas on an
   * Expert Program the experts prepare it and the cohort reads it. The panel
   * knows which side the viewer is on; this component does not.
   */
  sessionPrepCta?: string;
}

function formatTimeAgo(isoString: string): string {
  const now = new Date();
  const date = parseDateInput(isoString);
  if (!date) return "just now";
  const days = differenceInDays(now, date);
  if (days >= 1) return `${days}d ago`;
  const hours = differenceInHours(now, date);
  if (hours >= 1) return `${hours}h ago`;
  const minutes = differenceInMinutes(now, date);
  if (minutes >= 1) return `${minutes}m ago`;
  return "just now";
}

type IconConfig = {
  icon: React.ComponentType<{ className?: string }>;
  // Uses only CSS variables defined in global.css / tailwind.config aliases
  containerClass: string;
  iconClass: string;
};

function getIconConfig(notificationType: string): IconConfig {
  switch (notificationType) {
    case "linkedin_connected":
      return {
        icon: Link2,
        containerClass: "bg-[var(--color-linkedin-bg)]",
        iconClass: "text-[var(--color-linkedin-icon)]",
      };
    case "north_star_set":
      return {
        icon: Star,
        containerClass: "bg-theme-bg-warning",
        iconClass: "text-theme-text-warning",
      };
    case "onboarding_complete":
      return {
        icon: CheckCircle2,
        containerClass: "bg-theme-bg-success",
        iconClass: "text-theme-text-success",
      };
    case "achiever_goals_finalized":
      return {
        icon: ClipboardList,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-link",
      };
    case "new_matching_round_available":
      return {
        icon: Users,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-link",
      };
    case "pab_formed":
      return {
        icon: Handshake,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-link",
      };
    case "advisor_accepted_invite":
      return {
        icon: UserCheck,
        containerClass: "bg-theme-bg-success",
        iconClass: "text-theme-text-success",
      };
    case "goal_achieved":
      return {
        icon: Trophy,
        containerClass: "bg-theme-bg-warning",
        iconClass: "text-theme-text-warning",
      };
    case "meeting_scheduled":
      return {
        icon: CalendarCheck,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-link",
      };
    case "session_prep_ready":
      return {
        icon: ClipboardList,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-link",
      };
    case "goal_achieved_celebration":
    case "first_goal_achieved":
      return {
        icon: PartyPopper,
        containerClass: "bg-theme-bg-warning",
        iconClass: "text-theme-text-warning",
      };
    case "advisor_tagged_in_goal":
      return {
        icon: UserCheck,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-link",
      };
    case "task_overdue":
      return {
        icon: AlertCircle,
        containerClass: "bg-theme-bg-error",
        iconClass: "text-theme-text-error",
      };
    case "pab_request_received":
    case "pab_invitation_sent":
      return {
        icon: UserPlus,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-link",
      };
    case "board_invitation_accepted":
    case "pab_request_accepted_confirmation":
      return {
        icon: UserCheck,
        containerClass: "bg-theme-bg-success",
        iconClass: "text-theme-text-success",
      };
    case "board_invitation_declined":
    case "advisor_declined_invite":
      return {
        icon: UserX,
        containerClass: "bg-theme-bg-error",
        iconClass: "text-theme-text-error",
      };
    case "pab_fully_formed":
      return {
        icon: Handshake,
        containerClass: "bg-theme-bg-success",
        iconClass: "text-theme-text-success",
      };
    case "session_booking_confirmed":
    case "group_session_invitation":
      return {
        icon: CalendarCheck,
        containerClass: "bg-theme-bg-success",
        iconClass: "text-theme-text-success",
      };
    case "session_cancelled_by_advisor":
    case "session_cancelled_by_achiever":
      return {
        icon: CalendarX,
        containerClass: "bg-theme-bg-error",
        iconClass: "text-theme-text-error",
      };
    case "session_rescheduled":
    case "session_prep_reminder_24h":
    case "session_starting_soon":
    case "prep_notes_reminder":
      return {
        icon: CalendarClock,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-link",
      };
    case "prep_notes_submitted":
      return {
        icon: Send,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-link",
      };
    case "calendar_sync_issue":
      return {
        icon: AlertCircle,
        containerClass: "bg-theme-bg-warning",
        iconClass: "text-theme-text-warning",
      };
    case "pab_request_expiring_soon":
    case "multiple_pending_requests":
      return {
        icon: Clock,
        containerClass: "bg-theme-bg-warning",
        iconClass: "text-theme-text-warning",
      };
    case "session_feedback_request":
      return {
        icon: ClipboardList,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-link",
      };
    case "program_graduation":
      return {
        icon: Trophy,
        containerClass: "bg-theme-bg-warning",
        iconClass: "text-theme-text-warning",
      };
    case "new_direct_message":
    case "unread_messages_digest":
      return {
        icon: MessageCircle,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-link",
      };
    default:
      return {
        icon: Bell,
        containerClass: "bg-theme-bg-surface-raised",
        iconClass: "text-theme-text-muted",
      };
  }
}

export const NotificationItem = ({
  notification,
  onClick,
  sessionPrepCta = "View Session Prep",
}: NotificationItemProps) => {
  const isUnread = !notification.isRead;
  const { icon: Icon, containerClass, iconClass } = getIconConfig(notification.notificationType);
  const isMessageType = MESSAGE_NOTIFICATION_TYPES.has(notification.notificationType);
  const isSessionPrepType = SESSION_PREP_NOTIFICATION_TYPES.has(notification.notificationType);

  return (
    <button
      type="button"
      onClick={() => onClick(notification)}
      className={[
        "w-full text-left px-4 py-3.5 transition-all duration-150 border-b border-theme-border last:border-b-0 group",
        notification.isActionable
          ? "cursor-pointer hover:bg-theme-bg-surface-raised"
          : "cursor-default hover:bg-theme-bg-surface-raised",
        isUnread ? "bg-[var(--theme-accent-subtle)]/40" : "bg-theme-bg-surface",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        {/* Type icon */}
        <div
          className={`flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${containerClass}`}
        >
          <Icon className={`h-4 w-4 ${iconClass}`} />
        </div>

        {/* Main content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span
              className={[
                "font-inter text-[13px] leading-[1.4]",
                isUnread ? "font-semibold text-theme-text-heading" : "font-medium text-theme-text",
              ].join(" ")}
            >
              {notification.title}
            </span>
            <div className="flex-shrink-0 flex items-center gap-1.5 mt-[1px]">
              {isUnread && (
                <span className="block h-1.5 w-1.5 rounded-full bg-[var(--color-primary-750)]" />
              )}
              <span className="font-inter text-[11px] text-theme-text-muted tabular-nums whitespace-nowrap">
                {formatTimeAgo(notification.createdAt)}
              </span>
            </div>
          </div>

          <p className="mt-0.5 line-clamp-3 font-inter text-[12px] text-theme-text-secondary leading-[1.5]">
            {notification.body}
          </p>

          {/* Actionable CTA */}
          {notification.isActionable && (
            <div className="mt-1.5 flex items-center gap-1 text-theme-text-link">
              <span className="font-inter text-[11px] font-semibold">
                {isMessageType
                  ? "View messages"
                  : isSessionPrepType
                    ? sessionPrepCta
                    : "View details"}
              </span>
              <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </div>
          )}
        </div>
      </div>
    </button>
  );
};
